import React, { useState, useEffect, useContext, useMemo, useRef } from 'react';
import { useUserEvent } from '../hooks';
import { usePlanLimits } from '../hooks/usePlanLimits';
import { ThemeContext } from '../App';
import { supabase } from '../supabaseClient';
import { loadVisibleAuthorizedProperties } from '../services/authorizedPropertiesService';
import { propertyActionAllowed } from '../utils/collaborationAccess';
import { loadPerformanceRecords } from '../services/performanceService';
import { agriculturalComparison, assignPerformanceCosts, buildPerformanceRows, currentYearPeriod, cycleLifecycle, cycleMetrics, hasValue, inPeriod, performanceDate, performanceKey as chaveDesempenho, previousComparableCycle, summarizePerformance, validatePeriod } from './performanceModel';
import UpgradeModal from '../components/UpgradeModal';
import DesempenhoPecuario from './DesempenhoPecuario';
import DesempenhoGeral from './DesempenhoGeral';
import PropertyFinance from './PropertyFinance';
import PerformancePeriod from './PerformancePeriod';
import PerformanceComparison, { formatPerformanceValue } from './PerformanceComparison';
import { createAgricultureWorkbook, savePerformanceWorkbook } from './performanceReports';
import { carregarLogoRelatorio, criarCabecalhoRelatorio, criarDocumentoRelatorio, criarTabelaRelatorio } from '../utils/reportLayout';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import {
  FaArrowLeft, FaBalanceScale, FaBullseye, FaChartLine, FaCheck,
  FaCheckCircle, FaClipboardList, FaCoins, FaExclamationTriangle, FaLeaf,
  FaFileExcel, FaFilePdf, FaLock, FaMapMarkerAlt, FaPen, FaRulerCombined, FaSeedling,
  FaSpinner, FaWallet,
} from 'react-icons/fa';
import './Produtividade.css';

pdfMake.vfs = pdfFonts.pdfMake ? pdfFonts.pdfMake.vfs : pdfFonts;

const UNIDADES_PRODUCAO = {
  sc: 'Sacas (sc)', kg: 'Quilogramas (kg)', ton: 'Toneladas (ton)',
  '@': 'Arrobas (@)', st: 'Metros estéreos (st)',
};

// Unidade de medida comercial por cultura
const UNIDADE_CULTURA = {
  Soja: 'sc', Milho: 'sc', Feijão: 'sc', Trigo: 'sc', Arroz: 'sc',
  Café: 'sc', 'Café Conilon': 'sc', Algodão: '@',
  'Cana-de-açúcar': 'ton', Sorgo: 'sc', Girassol: 'sc',
  Amendoim: 'sc', Mandioca: 'ton', Eucalipto: 'st',
};

function useIsMobile(bp = 768) {
  const [v, setV] = useState(() => typeof window !== 'undefined' ? window.innerWidth < bp : false);
  useEffect(() => {
    const h = () => setV(window.innerWidth < bp);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, [bp]);
  return v;
}

function MetricCard({ icon, label, value, sub, tone, darkMode }) {
  return (
    <div className="crop-performance-metric" role="group" aria-label={label} style={{
      background: darkMode ? '#1f2937' : '#f8fafc',
      border: `1px solid ${darkMode ? '#374151' : '#e2e8f0'}`,
      borderRadius: 12, padding: '12px 14px',
      display: 'flex', flexDirection: 'column', gap: 3,
    }}>
      <div className="performance-metric-label" style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
        <span className="crop-performance-metric-icon">{icon}</span> {label}
      </div>
      <div className="performance-metric-value" data-tone={tone} style={{ fontWeight: 800 }}>
        {value}
      </div>
      {sub && <div className="performance-metric-help">{sub}</div>}
    </div>
  );
}

export default function Produtividade({ onBack, user, onNavigate, pecuariaEnabled = false }) {
  useUserEvent({ userId: user?.id, tela: 'Produtividade', evento: 'page_view', comDuração: true });
  const { darkMode } = useContext(ThemeContext);
  const isMobile = useIsMobile();
  const { plano, isStarter: personalStarter, podeUsar, loading: planLoading } = usePlanLimits(user?.id);
  const [showUpgrade, setShowUpgrade] = useState(false);

  const [propriedades, setPropriedades] = useState([]);
  const [propId, setPropId] = useState('');
  const currentProperty = propriedades.find(property => String(property.id) === String(propId));
  const sharedContext = Boolean(currentProperty?._collaborationPermissions);
  const performanceOwnerId = sharedContext ? currentProperty.user_id : user?.id;
  const isStarter = personalStarter && !sharedContext;
  const [talhoes, setTalhoes] = useState([]);
  const [ciclos, setCiclos] = useState([]);
  const [estimativas, setEstimativas] = useState({});
  const [lancamentos, setLancamentos] = useState([]);
  const [custosIndisponiveis, setCustosIndisponiveis] = useState(true);
  const [loading, setLoading] = useState(true);
  const [loadingTalhoes, setLoadingTalhoes] = useState(false);
  const [falhaConsulta, setFalhaConsulta] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [gerandoRelatorio, setGerandoRelatorio] = useState(false);
  const [erro, setErro] = useState('');
  const [mensagem, setMensagem] = useState('');
  const [editandoId, setEditandoId] = useState(null);
  const editorRef = useRef(null);
  const [form, setForm] = useState({});
  const [filtroStatus, setFiltroStatus] = useState('andamento');
  const [filtroCiclo, setFiltroCiclo] = useState('todos');
  const [filtroTalhao, setFiltroTalhao] = useState('todos');
  const [periodo, setPeriodo] = useState(currentYearPeriod);
  const [financeiroDisponivel, setFinanceiroDisponivel] = useState(false);
  const [periodoComparacao, setPeriodoComparacao] = useState(null);
  const [filtrarPlantio, setFiltrarPlantio] = useState(false);
  const [periodoPlantio, setPeriodoPlantio] = useState(currentYearPeriod);
  const [cicloComparacao, setCicloComparacao] = useState('');
  const [comparacaoAberta, setComparacaoAberta] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [visaoDesempenho, setVisaoDesempenho] = useState('geral');
  const canReadCosts = Boolean(currentProperty) && propertyActionAllowed(currentProperty, 'caderno', 'view', true);
  useEffect(() => {
    if (editandoId) editorRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
  }, [editandoId]);
  useEffect(() => { if (visaoDesempenho === 'pecuaria' && !pecuariaEnabled) setVisaoDesempenho('agricultura'); }, [visaoDesempenho, pecuariaEnabled]);

  // ─── Carrega propriedades (próprias + compartilhadas) ───────────────────────
  useEffect(() => {
    let active = true;
    setPropriedades([]);
    setPropId('');
    if (!user?.id) { setLoading(false); return; }
    (async () => {
      setLoading(true);
      setErro('');
      try {
        const { properties: lista } = await loadVisibleAuthorizedProperties(
          user.id,
          'id, nome, municipio, estado, user_id', 'produtividade'
        );
        if (!active) return;
        setPropriedades(lista);
        setPropId(previous => lista.some(property => String(property.id) === String(previous))
          ? previous : lista.length === 1 ? lista[0].id : '');
      } catch (error) {
        if (!active) return;
        console.error('Erro ao carregar propriedades de desempenho:', error);
        setErro('Não foi possível carregar as propriedades. Tente novamente em instantes.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [user?.id]);

  useEffect(() => {
    setFiltroCiclo('todos');
    setFiltroStatus('andamento');
    setFiltroTalhao('todos');
    setCicloComparacao('');
    setComparacaoAberta(false);
    setMensagem('');
  }, [propId]);

  // ─── Carrega talhões + estimativas + custos ao selecionar propriedade ───────
  useEffect(() => {
    let active = true;
    setTalhoes([]);
    setCiclos([]);
    setEstimativas({});
    setLancamentos([]);
    setEditandoId(null);
    setFalhaConsulta(false);
    setCustosIndisponiveis(true);
    if (!propId || !currentProperty || !user?.id) {
      setLoadingTalhoes(false);
      if (propId && !currentProperty) {
        setFalhaConsulta(true);
        setErro('Seu acesso não permite consultar o desempenho agrícola e os gastos desta propriedade.');
      }
      return;
    }
    setLoadingTalhoes(true);
    setErro('');
    (async () => {
      try {
        const records = await loadPerformanceRecords(propId, performanceOwnerId, canReadCosts);
        if (!active) return;
        setTalhoes(records.plots);
        setCiclos(records.cycles);
        setLancamentos(records.entries);
        setCustosIndisponiveis(Boolean(records.costsError));
        if (records.costsError) {
          console.error('Erro ao carregar gastos de desempenho:', records.costsError);
          setErro(`Gastos indisponíveis. ${records.costsError.message || 'Atualize antes de considerar os totais.'}`);
        }
        const estMap = {};
        records.estimates.forEach(e => {
          estMap[chaveDesempenho(e.talhao_id, e.ciclo_cultivo_id)] = {
            id: e.id, cicloId: e.ciclo_cultivo_id || null,
            produtividade: e.produtividade_estimada?.toString() ?? '',
            cotacao: e.cotacao_unitaria?.toString() ?? '',
            real: e.produtividade_real?.toString() ?? '',
            unidade: e.unidade || 'sc',
          };
        });
        setEstimativas(estMap);
      } catch (error) {
        if (!active) return;
        setFalhaConsulta(true);
        console.error('Erro ao carregar desempenho:', error);
        setErro('Não foi possível carregar o desempenho completo. Tente atualizar a consulta.');
      } finally {
        if (active) setLoadingTalhoes(false);
      }
    })();
    return () => { active = false; };
  }, [propId, currentProperty, user?.id, performanceOwnerId, canReadCosts, refreshKey]);

  function abrirEdicao(talhao) {
    if (planLoading || isStarter) { setShowUpgrade(true); return; }
    if (talhao._readOnly) { setErro('Este plantio físico ainda não possui vínculo para gravar metas de produtividade. Os dados permanecem somente para consulta.'); return; }
    const est = estimativas[talhao._performanceKey] || {};
    if (!propertyActionAllowed(currentProperty, 'produtividade', est.id ? 'edit' : 'create', true)) { setErro('Seu acesso permite consultar, mas não alterar esta meta.'); return; }
    const unidPadrao = UNIDADE_CULTURA[talhao.cultura] || 'sc';
    setForm({
      produtividade: est.produtividade ?? '',
      cotacao: est.cotacao ?? '',
      real: est.real ?? '',
      unidade: est.unidade || unidPadrao,
    });
    setMensagem('');
    setErro('');
    setEditandoId(talhao._performanceKey);
  }

  async function salvar(talhao) {
    if (salvando) return;
    if (planLoading || isStarter) { setShowUpgrade(true); return; }
    if (talhao._readOnly) { setErro('Este plantio ainda não permite gravar metas.'); return; }
    if (['produtividade', 'cotacao', 'real'].some(key => form[key] !== '' && (!hasValue(form[key]) || Number(form[key]) < 0))) {
      setErro('Informe valores numéricos maiores ou iguais a zero.');
      return;
    }
    if (!estimativas[talhao._performanceKey]?.id && !['produtividade', 'cotacao', 'real'].some(key => hasValue(form[key]))) {
      setErro('Preencha pelo menos a meta, o preço esperado ou a produtividade real para salvar.');
      return;
    }
    const existing = estimativas[talhao._performanceKey];
    if (!propertyActionAllowed(currentProperty, 'produtividade', existing?.id ? 'edit' : 'create', true)) { setErro('Alteração não autorizada nesta propriedade.'); return; }
    setSalvando(true);
    const payload = {
      talhao_id: talhao.id,
      ciclo_cultivo_id: talhao._ciclo?.id || null,
      user_id: performanceOwnerId,
      produtividade_estimada: form.produtividade ? parseFloat(form.produtividade) : null,
      cotacao_unitaria: form.cotacao ? parseFloat(form.cotacao) : null,
      produtividade_real: form.real ? parseFloat(form.real) : null,
      unidade: form.unidade || 'sc',
    };

    setErro('');
    try {
      if (existing?.id) {
        const { data, error } = await supabase.from('produtividade_talhao').update(payload).eq('id', existing.id).select('id').single();
        if (error) throw error;
        if (!data?.id) throw new Error('Atualização da meta não confirmada pelo banco.');
        setEstimativas(prev => ({ ...prev, [talhao._performanceKey]: { ...prev[talhao._performanceKey], ...form } }));
      } else {
        const { data, error } = await supabase.from('produtividade_talhao').insert(payload).select().single();
        if (error) throw error;
        if (!data?.id) throw new Error('Gravação da meta não confirmada pelo banco.');
        setEstimativas(prev => ({ ...prev, [talhao._performanceKey]: { ...form, id: data.id, cicloId: talhao._ciclo?.id || null } }));
      }
      setEditandoId(null);
      setMensagem('Dados do plantio salvos.');
    } catch (error) {
      console.error('Erro ao salvar meta de desempenho:', error);
      setErro('Não foi possível salvar a meta deste talhão. Nenhuma alteração foi aplicada.');
    } finally {
      setSalvando(false);
    }
  }

  const todosCiclos = useMemo(() => buildPerformanceRows(talhoes, ciclos), [talhoes, ciclos]);
  const conciliacao = useMemo(() => assignPerformanceCosts(lancamentos, todosCiclos, talhoes), [lancamentos, todosCiclos, talhoes]);
  const custos = conciliacao.costs;
  const ciclosHistoricos = useMemo(() => filtrarPlantio ? todosCiclos.filter(row => inPeriod(row.data_plantio, periodoPlantio)) : todosCiclos,
    [todosCiclos, filtrarPlantio, periodoPlantio]);
  const ciclosDoTalhao = useMemo(() => ciclosHistoricos.filter(row =>
    filtroTalhao === 'todos' || row._plotKey === filtroTalhao), [ciclosHistoricos, filtroTalhao]);
  const ciclosPorStatus = useMemo(() => ciclosHistoricos.filter(row => {
    if (filtroStatus === 'andamento') return cycleLifecycle(row) !== 'harvested';
    if (filtroStatus === 'colhidos') return cycleLifecycle(row) === 'harvested';
    return true;
  }), [ciclosHistoricos, filtroStatus]);
  const ciclosDisponiveis = useMemo(() => ciclosPorStatus.filter(row =>
    filtroTalhao === 'todos' || row._plotKey === filtroTalhao), [ciclosPorStatus, filtroTalhao]);
  const talhoesFiltrados = useMemo(() => ciclosDisponiveis.filter(row =>
    filtroCiclo === 'todos' || row._performanceKey === filtroCiclo), [ciclosDisponiveis, filtroCiclo]);
  const resumo = useMemo(() => summarizePerformance(talhoesFiltrados, estimativas, custos), [talhoesFiltrados, estimativas, custos]);
  const referencia = todosCiclos.find(row => row._performanceKey === cicloComparacao);
  const metricasComparacao = referencia ? agriculturalComparison(resumo, summarizePerformance([referencia], estimativas, custos), custosIndisponiveis) : [];
  const cicloLabel = row => `${row.cultura} · plantio ${row.data_plantio.split('-').reverse().join('/')} · ${row.nome}`;
  const talhoesDisponiveis = [...new Map(ciclosPorStatus.map(row => [row._plotKey, row.nome])).entries()];
  const cicloAnterior = talhoesFiltrados.length === 1 ? previousComparableCycle(talhoesFiltrados[0], todosCiclos) : null;
  const faltaResultado = row => cycleLifecycle(row) === 'harvested' && !hasValue(estimativas[row._performanceKey]?.real);
  const pendencias = talhoesFiltrados.filter(row => faltaResultado(row) || !(Number(estimativas[row._performanceKey]?.produtividade) > 0));
  const selecaoLabel = talhoesFiltrados.length === 1 ? cicloLabel(talhoesFiltrados[0])
    : `${talhoesFiltrados.length} ciclos selecionados${filtrarPlantio ? ` · plantios de ${periodoPlantio.start} a ${periodoPlantio.end}` : ''}`;
  const plantioLabel = talhoesFiltrados.length === 1 ? 'plantio' : 'plantios';
  const filtroHistoricoValido = !filtrarPlantio || !validatePeriod(periodoPlantio);
  useEffect(() => {
    if (!loadingTalhoes && filtroCiclo !== 'todos' && !ciclosDisponiveis.some(row => row._performanceKey === filtroCiclo)) setFiltroCiclo('todos');
    if (!loadingTalhoes && filtroTalhao !== 'todos' && !ciclosPorStatus.some(row => row._plotKey === filtroTalhao)) setFiltroTalhao('todos');
  }, [loadingTalhoes, ciclosDisponiveis, ciclosPorStatus, filtroCiclo, filtroTalhao]);

  function conferirGastos() {
    const dates = conciliacao.assignments.map(entry => performanceDate(entry.date))
      .filter(date => date && !validatePeriod({ start: date, end: date })).sort();
    if (!dates.length) { setErro('Os gastos não possuem datas válidas para consultar o histórico. Confira os lançamentos de origem.'); return; }
    setPeriodo({ start: dates[0], end: dates[dates.length - 1] });
    setVisaoDesempenho('geral');
  }

  // ─── Helpers ────────────────────────────────────────────────────────────────
  const fmtBRL = v => Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });
  const fmtNum = (v, dec = 1) => Number(v).toLocaleString('pt-BR', { minimumFractionDigits: dec, maximumFractionDigits: dec });
  const fmtProducao = producaoPorUnidade => Object.entries(producaoPorUnidade)
    .map(([unidade, valor]) => `${fmtNum(valor, 0)} ${unidade}`)
    .join(' · ');
  const lifecycle = cycleLifecycle;
  const isColhido = t => lifecycle(t) === 'harvested';
  const isPlanejado = t => lifecycle(t) === 'planned';

  const produtividades = Object.keys({ ...resumo.producaoPorUnidade, ...resumo.producaoReal }).map(key => {
    const real = resumo.areaComResultado[key] > 0;
    const area = real ? resumo.areaComResultado[key] : resumo.areaComMetaPorUnidade[key];
    const production = real ? resumo.producaoReal[key] : resumo.producaoPorUnidade[key];
    return area > 0 ? `${key}: ${fmtNum(production / area)}/ha · ${real ? 'real' : 'meta'}` : `${key}: área não informada`;
  });

  // ─── Filtro por status (em desenvolvimento / colhidos) ───────────────────────
  const contagemStatus = useMemo(() => {
    let desenvolvimento = 0, planejados = 0, colhidos = 0;
    ciclosDoTalhao.forEach(t => {
      if (cycleLifecycle(t) === 'harvested') colhidos++;
      else if (cycleLifecycle(t) === 'planned') planejados++;
      else desenvolvimento++;
    });
    return { desenvolvimento, planejados, colhidos };
  }, [ciclosDoTalhao]);
  function autorizarExportacao(format) {
    if (planLoading) return false;
    if (format === 'pdf' && !sharedContext && !podeUsar('relatorios_pdf')) {
      setShowUpgrade(true);
      return false;
    }
    return true;
  }

  async function exportarRelatorio(format = 'pdf') {
    if (gerandoRelatorio || !autorizarExportacao(format)) return;
    if (custosIndisponiveis) {
      setErro('Os gastos estão indisponíveis. Atualize a página antes de exportar os totais.');
      return;
    }
    if (!filtroHistoricoValido || talhoesFiltrados.length === 0) {
      setErro('Não há talhões nos filtros selecionados para gerar o relatório.');
      return;
    }

    setGerandoRelatorio(true);
    setErro('');
    try {
      const statusLabel = {
        todos: 'Todos os estágios',
        andamento: 'Em andamento (inclui planejados)',
        colhidos: 'Concluídas',
      }[filtroStatus];
      const filterLabel = `${statusLabel} · ${selecaoLabel} · ${filtroTalhao !== 'todos' ? `Talhão: ${talhoesDisponiveis.find(([id]) => id === filtroTalhao)?.[1]} · ` : ''}${filtrarPlantio ? `Plantios de ${periodoPlantio.start} a ${periodoPlantio.end}` : 'Todos os anos de plantio'}`;
      const nomeSeguro = String(propAtual?.nome || 'propriedade').normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-zA-Z0-9]+/g, '_').toLowerCase();
      if (format === 'xlsx') {
        await savePerformanceWorkbook(createAgricultureWorkbook({
          rows: talhoesFiltrados, estimates: estimativas, costs: custos, context: propAtual?.nome,
          filterLabel,
          comparison: referencia ? {
            metrics: metricasComparacao, currentLabel: selecaoLabel,
            previousLabel: cicloLabel(referencia),
          } : null,
        }), `desempenho_ciclos_${nomeSeguro}.xlsx`);
        return;
      }
      const logoBase64 = await carregarLogoRelatorio();
      const geradoEm = new Date();

      const resumoFiltrado = resumo;
      const body = talhoesFiltrados.map(talhao => {
        const est = estimativas[talhao._performanceKey];
        const { area, cost: custo, revenue: receita, margin: margem } = cycleMetrics(talhao, est, custos);
        const unidade = est?.unidade || UNIDADE_CULTURA[talhao.cultura] || 'sc';
        const cell = value => String(value ?? '-');
        return [
          cell(`${talhao.nome}\n${talhao.data_plantio} · ${talhao.data_colheita_efetiva || 'em aberto'}`),
          cell(talhao.cultura || '-'),
          cell(`${fmtNum(area)} ha`),
          cell(est?.produtividade ? `${fmtNum(est.produtividade)} ${unidade}/ha` : '-'),
          cell(hasValue(est?.real) ? `${fmtNum(est.real)} ${unidade}/ha` : '-'),
          cell(custo != null ? fmtBRL(custo) : 'Sem registros'),
          cell(receita != null ? fmtBRL(receita) : '-'),
          cell(margem != null ? fmtBRL(margem) : '-'),
        ];
      });
      const cycleTable = criarTabelaRelatorio([
        ['TALHÃO', 'CULTURA', 'ÁREA', 'META', 'REAL', 'CUSTO', 'RECEITA PROJ.', 'SALDO PROJ.'],
        ...body,
      ], true);
      cycleTable.table.widths = ['*', 65, 48, 68, 68, 64, 68, 68];
      cycleTable.fontSize = 7;
      const docDefinition = {
        ...criarDocumentoRelatorio([], geradoEm),
        pageOrientation: 'landscape',
        content: [
          criarCabecalhoRelatorio('Relatório de Desempenho dos Ciclos Agrícolas', {
            propriedade: propAtual?.nome || 'Propriedade', area: `${talhoesFiltrados.length} ciclos selecionados`,
          }, logoBase64, geradoEm),
          { text: `Filtro: ${filterLabel}`, fontSize: 8, margin: [0, 0, 0, 3] },
          { text: filtrarPlantio ? `Plantios de ${periodoPlantio.start} a ${periodoPlantio.end}` : 'Todos os anos de plantio', fontSize: 8, margin: [0, 0, 0, 8] },
          {
            table: {
              widths: ['*', '*', '*', '*'],
              body: [[
                { text: `ÁREA FÍSICA\n${fmtNum(resumoFiltrado.area)} ha`, style: 'metric' },
                { text: `ÁREA CULTIVADA ACUMULADA\n${fmtNum(resumoFiltrado.areaCultivada)} ha`, style: 'metric' },
                { text: `CUSTOS\n${resumoFiltrado.comCustos ? fmtBRL(resumoFiltrado.custo) : 'Sem registros'}`, style: 'metric' },
                { text: `SALDO PARCIAL PROJETADO\n${resumoFiltrado.receita && resumoFiltrado.margem != null ? fmtBRL(resumoFiltrado.margem) : 'Dados insuficientes'}`, style: 'metric' },
              ]],
            },
            layout: { hLineColor: () => '#cfe4df', vLineColor: () => '#cfe4df', paddingTop: () => 8, paddingBottom: () => 8 },
            margin: [0, 0, 0, 12],
          },
          cycleTable,
          ...(referencia ? [
            { text: `COMPARAÇÃO COM ${cicloLabel(referencia)}`, fontSize: 10, bold: true, margin: [0, 12, 0, 6] },
            { ...criarTabelaRelatorio([
              ['INDICADOR', 'SELECIONADO', 'REFERÊNCIA'],
              ...metricasComparacao.map(metric => [metric.label, formatPerformanceValue(metric.current, metric.currency), formatPerformanceValue(metric.previous, metric.currency)]),
            ], true), fontSize: 8 },
          ] : []),
          { text: 'Custos associados por correspondência única de data e talhão, sem rateio. Gastos gerais e sem ciclo compatível estão na visão Geral. Ausência de lançamentos não comprova custo zero. Projeções não representam vendas nem lucro realizado.', fontSize: 8, margin: [0, 12, 0, 0] },
          { text: 'Histórico consultado nos registros atuais, com área e metas atuais. Não é um fechamento imutável; alterações posteriores podem mudar os resultados. Custo por hectare exige gastos e área em todos os ciclos selecionados. Custo por unidade usa ciclos com resultado informado, exigindo área e gastos em todos eles, por cultura/unidade.', fontSize: 8, margin: [0, 6, 0, 0] },
        ],
        styles: {
          metric: { alignment: 'center', color: '#1976d2', bold: true, fontSize: 8 },
        },
      };
      pdfMake.createPdf(docDefinition).download(`desempenho_ciclos_${nomeSeguro}.pdf`);
    } catch (error) {
      console.error('Erro ao gerar relatório de desempenho:', error);
      setErro('Não foi possível gerar o relatório. Tente novamente.');
    } finally {
      setGerandoRelatorio(false);
    }
  }

  // ─── Estilos base ────────────────────────────────────────────────────────────
  const padL = `calc(env(safe-area-inset-left, 0px) + ${isMobile ? '14px' : '24px'})`;
  const padR = `calc(env(safe-area-inset-right, 0px) + ${isMobile ? '14px' : '24px'})`;  // right side uses its own inset
  const bg = darkMode ? '#111827' : '#f0f4f8';
  const cardBg = darkMode ? '#1a2234' : '#ffffff';
  const border = darkMode ? '#1f2937' : '#e8edf3';
  const textPrimary = 'var(--crop-ink)';
  const textSecondary = 'var(--crop-muted)';
  const inputStyle = {
    width: '100%', padding: isMobile ? '12px' : '9px 12px', borderRadius: 8,
    fontSize: 16,  // ≥16px prevents iOS auto-zoom on focus
    border: `1.5px solid ${darkMode ? '#374151' : '#cbd5e1'}`,
    background: 'var(--crop-panel)',
    color: textPrimary, outline: 'none', boxSizing: 'border-box',
    WebkitAppearance: 'none',
  };

  const propAtual = currentProperty;

  return (
    <div className={`crop-performance-shell ${darkMode ? 'is-dark' : ''}`} style={{
      minHeight: '100vh', background: bg,
      paddingTop: `calc(env(safe-area-inset-top, 0px) + ${isMobile ? '12px' : '24px'})`,
      paddingLeft: padL, paddingRight: padR,
      paddingBottom: `calc(env(safe-area-inset-bottom, 0px) + 24px)`,
    }}>

      {/* ── Header sticky ────────────────────────────────────────────────────── */}
      <div className="crop-performance-topbar" style={{
        position: 'sticky', top: 0, zIndex: 20,
        background: darkMode ? '#111827' : '#fff',
        marginLeft: `calc(-1 * (env(safe-area-inset-left, 0px) + ${isMobile ? '14px' : '24px'}))`,
        marginRight: `calc(-1 * (env(safe-area-inset-right, 0px) + ${isMobile ? '14px' : '24px'}))`,
        paddingLeft: padL, paddingRight: padR,
        paddingTop: isMobile ? 10 : 14, paddingBottom: isMobile ? 10 : 14,
        borderBottom: `1px solid ${border}`,
        boxShadow: '0 2px 12px rgba(0,0,0,0.07)', marginBottom: 20,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 10 : 14 }}>
          {/* Botão voltar */}
          <button className="crop-performance-back" aria-label="Voltar" onClick={onBack} style={{
            flexShrink: 0, background: 'none', border: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 6,
            padding: isMobile ? '10px 4px' : '6px 4px', minHeight: 44,
            color: darkMode ? '#60a5fa' : '#0284c7',
            fontSize: isMobile ? 13 : 14, fontWeight: 600, borderRadius: 6,
            WebkitTapHighlightColor: 'transparent',
          }}>
            <FaArrowLeft />
            {!isMobile && <span>Voltar</span>}
          </button>

          <div style={{ width: 1, height: isMobile ? 28 : 32, background: border, flexShrink: 0 }} />

          <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 10 : 12, minWidth: 0 }}>
            <div className="crop-performance-brand-icon" style={{
              width: isMobile ? 34 : 40, height: isMobile ? 34 : 40,
              borderRadius: 11, flexShrink: 0,
              background: 'linear-gradient(135deg, #43a047, #2e7d32)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: isMobile ? 16 : 20,
              boxShadow: '0 3px 10px rgba(67,160,71,0.35)',
            }}><FaChartLine /></div>
            <div style={{ minWidth: 0 }}>
              <h1 className="crop-performance-title" style={{ fontSize: isMobile ? 18 : 20, fontWeight: 700, margin: 0 }}>
                Desempenho da Propriedade
              </h1>
              <div className="crop-performance-property-name" style={{ fontSize: 13, marginTop: 3, overflowWrap: 'anywhere' }}>
                {propAtual ? `${propAtual.nome} · ${propAtual.municipio}/${propAtual.estado}` : 'Acompanhe o que foi gasto, planejado e produzido'}
              </div>
            </div>
          </div>
        </div>
      </div>

      {(
        <nav className="crop-performance-filters crop-performance-mode-tabs" aria-label="Tipo de desempenho" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 20 }}>
          {[{ id: 'geral', label: 'Geral' }, { id: 'agricultura', label: 'Agricultura' }, ...(pecuariaEnabled ? [{ id: 'pecuaria', label: 'Pecuária' }] : [])].map(op => {
            const ativo = visaoDesempenho === op.id;
            return (
              <button
                key={op.id}
                type="button"
                disabled={salvando}
                aria-pressed={ativo}
                onClick={() => setVisaoDesempenho(op.id)}
                style={{
                  minHeight: 38, padding: '0 18px', borderRadius: 20,
                  fontSize: 13, fontWeight: 700, cursor: 'pointer',
                  background: ativo ? 'linear-gradient(135deg, #43a047, #2e7d32)' : (darkMode ? '#1f2937' : '#f1f5f9'),
                  color: ativo ? '#fff' : textSecondary,
                  border: `1.5px solid ${ativo ? 'transparent' : border}`,
                  WebkitTapHighlightColor: 'transparent',
                }}
              >
                {op.label}
              </button>
            );
          })}
        </nav>
      )}

      <section className="performance-guide" aria-label="Como usar o desempenho">
        <h2>{visaoDesempenho === 'agricultura' ? 'Como está a sua safra?' : visaoDesempenho === 'pecuaria' ? 'Como está a sua criação?' : 'Comece por aqui'}</h2>
        <p>{visaoDesempenho === 'agricultura'
          ? 'Compare o que você esperava colher com o que realmente colheu e acompanhe os gastos de cada plantio.'
          : visaoDesempenho === 'pecuaria'
            ? 'Acompanhe as vendas no período escolhido. Os lotes mostram a situação atual do rebanho.'
            : 'Acompanhe a mesma propriedade por visões diferentes, sem confundir produção, vendas e lucro.'}</p>
        {visaoDesempenho === 'geral' && <>
          <dl className="performance-glossary">
            <div><dt>Geral: quanto a propriedade está rendendo?</dt><dd>Mostra lucro ou prejuízo estimado, margem e retorno dos gastos, pelos recebimentos e gastos pagos da atividade.</dd></div>
            <div><dt>Agricultura: como foi cada plantio?</dt><dd>Mostra metas, produção e gastos associados a cada ciclo completo, mesmo quando atravessa anos.</dd></div>
            {pecuariaEnabled && <div><dt>Pecuária: quanto foi vendido?</dt><dd>Mostra vendas por período e a situação atual dos lotes. Pastagens apoiam a criação, mas não viram receita agrícola automaticamente.</dd></div>}
          </dl>
          <p className="performance-notice">Geral e Agricultura consultam os mesmos lançamentos de gastos, com filtros diferentes: não some os totais das duas visões. Os indicadores usam recebimentos e gastos pagos da atividade, separando empréstimos, aportes, retiradas e bens. Não exigem saldo inicial, estoque ou depreciação; são estimativas, não lucro contábil. Você informa a divisão dos valores compartilhados. Projeções agrícolas não são vendas nem lucro.</p>
        </>}
        {visaoDesempenho === 'agricultura' && <ol className="performance-steps">
          <li><strong>Escolha o plantio</strong><span>Filtre a safra e o talhão abaixo.</span></li>
          <li><strong>Confira os indicadores</strong><span>Veja produção, gastos e metas.</span></li>
          <li><strong>Complete os dados</strong><span>Informe a meta ou o que colheu no cartão do talhão.</span></li>
        </ol>}
      </section>

      {!loading && propId && (
        <PropertyFinance key={propId} propertyId={propId} propertyName={propAtual?.nome}
          period={periodo} onPeriodChange={setPeriodo} view={visaoDesempenho}
          comparisonPeriod={periodoComparacao} onComparisonChange={setPeriodoComparacao}
          onBeforeExport={autorizarExportacao} exportDisabled={planLoading}
          onOpenGeneral={() => setVisaoDesempenho('geral')} refreshKey={refreshKey} onReady={setFinanceiroDisponivel}
          onRefresh={setMensagem} />
      )}

      {visaoDesempenho === 'pecuaria' ? (
        <DesempenhoPecuario propertyId={propId} onPropertyChange={setPropId} darkMode={darkMode} onNavigate={onNavigate} period={periodo} onPeriodChange={setPeriodo} comparisonPeriod={periodoComparacao} onComparisonChange={setPeriodoComparacao} disabled={salvando} onDataChanged={() => setRefreshKey(value => value + 1)} onBeforeExport={autorizarExportacao} exportDisabled={planLoading} />
      ) : <>

      {/* ── Seletor de propriedade ─────────────────────────────────────────── */}
      {!loading && propriedades.length > 1 && (
        <label className="crop-performance-property-select">
          <span><FaMapMarkerAlt /> Propriedade</span>
          <select
            aria-label="Propriedade do desempenho"
            value={propId}
            disabled={salvando}
            onChange={e => { setPropId(e.target.value); setEditandoId(null); }}
          >
            <option value="">Selecione uma propriedade…</option>
            {propriedades.map(p => <option key={p.id} value={p.id}>{p.nome} — {p.municipio}/{p.estado}</option>)}
          </select>
        </label>
      )}

      {!loading && propId && (
        <div className="crop-performance-actions" role="toolbar" aria-label="Ações do desempenho">
          <button type="button" className="crop-performance-report-action" disabled={loadingTalhoes || salvando} onClick={() => setRefreshKey(value => value + 1)}>Atualizar dados</button>
        </div>
      )}
      {erro && <div className="crop-performance-error" role="alert"><FaExclamationTriangle /><span>{erro}</span></div>}
      {mensagem && <p className="performance-save-status" role="status"><FaCheckCircle aria-hidden="true" /> {mensagem}</p>}

      {/* ── Estados vazios ────────────────────────────────────────────────── */}
      {loading && (
        <div className="crop-performance-empty" style={{ textAlign: 'center', padding: '60px 20px', color: textSecondary }}>
          <FaSpinner className="crop-performance-spinner" />
          <strong>Carregando propriedades</strong>
          <span>Organizando os indicadores disponíveis.</span>
        </div>
      )}

      {!loading && !propId && (
        <div className="crop-performance-empty" style={{ textAlign: 'center', padding: '60px 20px', color: textSecondary }}>
          <FaLeaf />
          <strong>Selecione uma propriedade</strong>
          <span>Escolha a área acima para analisar gastos por período e resultados dos ciclos produtivos.</span>
        </div>
      )}

      {!loading && propId && loadingTalhoes && (
        <div style={{ textAlign: 'center', padding: '40px 20px', color: textSecondary }}>Carregando talhões…</div>
      )}

      {!loading && propId && !loadingTalhoes && !falhaConsulta && visaoDesempenho === 'geral' && (
        <DesempenhoGeral integrated={financeiroDisponivel} propertyId={propId} propertyName={propAtual?.nome} assignments={conciliacao.assignments} costsUnavailable={custosIndisponiveis} period={periodo} onPeriodChange={setPeriodo} pecuariaEnabled={pecuariaEnabled && propertyActionAllowed(currentProperty, 'pecuaria', 'view', true)} refreshKey={refreshKey} onNavigate={onNavigate} onOpenAgriculture={() => setVisaoDesempenho('agricultura')} comparisonPeriod={periodoComparacao} onComparisonChange={setPeriodoComparacao} onBeforeExport={autorizarExportacao} exportDisabled={planLoading} />
      )}
      {!loading && propId && !loadingTalhoes && !falhaConsulta && visaoDesempenho === 'agricultura' && todosCiclos.length === 0 && (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: textSecondary }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🌱</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: textPrimary }}>Nenhum ciclo agrícola disponível</div>
          <div style={{ fontSize: 13, marginTop: 6 }}>Cadastre um plantio em Propriedades e Talhões. Gastos sem ciclo permanecem na visão Geral.</div>
        </div>
      )}

      {!loading && propId && !loadingTalhoes && !falhaConsulta && visaoDesempenho === 'agricultura' && todosCiclos.length > 0 && (
        <>
          <h2 className="performance-step-heading">1. Escolha o que deseja analisar</h2>
          <nav className="crop-performance-filters performance-stage-tabs" aria-label="Situação da safra">
            {[
              { id: 'andamento', label: `Em andamento (${contagemStatus.desenvolvimento + contagemStatus.planejados})` },
              { id: 'colhidos', label: `Concluídas (${contagemStatus.colhidos})` },
              { id: 'todos', label: `Todas (${ciclosDoTalhao.length})` },
            ].map(option => <button type="button" key={option.id} aria-pressed={filtroStatus === option.id}
              className={filtroStatus === option.id ? 'is-active' : ''} disabled={salvando}
              onClick={() => { setFiltroStatus(option.id); setEditandoId(null); }}>{option.label}</button>)}
          </nav>
          <div className="performance-selection">
            <label className="crop-performance-property-select">
              <span><FaSeedling /> Safra / ciclo</span>
              <select aria-label="Ciclo analisado" value={filtroCiclo} disabled={salvando} onChange={event => {
                setFiltroCiclo(event.target.value); setEditandoId(null); setCicloComparacao('');
              }}>
                <option value="todos">{filtroStatus === 'andamento' ? 'Todos os ciclos em andamento' : filtroStatus === 'colhidos' ? 'Todos os ciclos concluídos' : 'Todos os ciclos'}</option>
                {ciclosDisponiveis.map(row => <option key={row._performanceKey} value={row._performanceKey}>{cicloLabel(row)}</option>)}
              </select>
            </label>
            <label className="crop-performance-property-select">
              <span><FaMapMarkerAlt /> Talhão</span>
              <select aria-label="Talhão analisado" value={filtroTalhao} disabled={salvando || filtroCiclo !== 'todos'} onChange={event => {
                setFiltroTalhao(event.target.value); setEditandoId(null); setCicloComparacao('');
              }}>
                <option value="todos">{filtroCiclo !== 'todos' ? 'Talhão do ciclo selecionado' : 'Todos os talhões'}</option>
                {talhoesDisponiveis.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
              </select>
            </label>
          </div>
          <details className="performance-disclosure">
            <summary>Filtrar histórico por período</summary>
          <label className="performance-toggle"><input type="checkbox" checked={filtrarPlantio} disabled={salvando} onChange={event => { setFiltrarPlantio(event.target.checked); setFiltroCiclo('todos'); setEditandoId(null); }} />Consultar histórico por data de plantio</label>
          {filtrarPlantio && <fieldset className="performance-history-fields" disabled={salvando}><PerformancePeriod period={periodoPlantio} onChange={value => { setPeriodoPlantio(value); setFiltroCiclo('todos'); setEditandoId(null); }} dates={todosCiclos.map(row => row.data_plantio)} /></fieldset>}
            <p className="performance-notice">Selecione o período de plantio. Os gastos continuam abrangendo o ciclo inteiro, mesmo quando a safra atravessa anos.</p>
          </details>
          {filtrarPlantio && <p className="performance-notice" role="status">Filtro ativo: plantios de {periodoPlantio.start} a {periodoPlantio.end}. <button className="performance-text-action" disabled={salvando} onClick={() => setFiltrarPlantio(false)}>Limpar período</button></p>}
          <details className="performance-disclosure" open={comparacaoAberta} onToggle={event => setComparacaoAberta(event.currentTarget.open)}>
            <summary>Comparar com outra safra</summary>
            <button type="button" className="crop-performance-report-action" disabled={!cicloAnterior || salvando}
              onClick={() => setCicloComparacao(cicloAnterior._performanceKey)}>Comparar com ciclo anterior</button>
            <p className="performance-notice">{cicloAnterior ? `Sugestão: ${cicloLabel(cicloAnterior)}. Mesmo talhão e cultura; você pode trocar a referência abaixo.`
              : 'Selecione um único ciclo para sugerir o último plantio concluído da mesma cultura e talhão, ou escolha uma referência abaixo.'}</p>
          <label className="crop-performance-property-select"><span>Comparar com ciclo</span><select aria-label="Comparar com ciclo" value={referencia ? cicloComparacao : ''} disabled={salvando} onChange={event => setCicloComparacao(event.target.value)}>
            <option value="">Sem comparação</option>{todosCiclos.map(row => <option key={row._performanceKey} value={row._performanceKey}>{cicloLabel(row)}</option>)}
          </select></label>
          {referencia && filtroHistoricoValido && talhoesFiltrados.length > 0 && <PerformanceComparison onBeforeExport={autorizarExportacao} exportDisabled={planLoading} metrics={metricasComparacao} context={propAtual?.nome}
            currentLabel={selecaoLabel} previousLabel={cicloLabel(referencia)} />}
            <p className="performance-notice">Custos por unidade usam somente ciclos com produção real informada, sem misturar culturas ou unidades. Falta de gastos nesses ciclos impede o cálculo.</p>
          </details>

          {filtroHistoricoValido && <>
          <h2 className="performance-step-heading">2. Entenda os resultados da seleção</h2>
          <section aria-label="Indicadores principais" className="performance-main-metrics">
            <MetricCard icon={<FaSeedling />} label="Produtividade" value={produtividades.length ? produtividades.join(' · ') : 'Ainda não informada'}
              sub={`Quanto foi colhido por hectare. ${resumo.comResultado} de ${talhoesFiltrados.length} ${plantioLabel} com resultado; sem resultado, mostra a meta.`} darkMode={darkMode} />
            <MetricCard icon={<FaClipboardList />} label="Gastos registrados" value={custosIndisponiveis ? 'Indisponíveis' : resumo.comCustos ? fmtBRL(resumo.custo) : 'Sem registros'}
              sub={custosIndisponiveis ? 'Atualize os dados antes de analisar' : `${resumo.comCustos} de ${talhoesFiltrados.length} ${plantioLabel} com gastos`} darkMode={darkMode} />
            <MetricCard icon={<FaRulerCombined />} label="Custo por hectare" value={!custosIndisponiveis && resumo.custoPorHa != null ? fmtBRL(resumo.custoPorHa) : 'Dados insuficientes'}
              sub="Gastos ÷ hectares cultivados. Exige área e gastos em todos os plantios selecionados." darkMode={darkMode} />
            <MetricCard icon={<FaBullseye />} label="Resultado em relação à meta" value={resumo.resultadosComparaveis ? `${resumo.metasAtingidas} de ${resumo.resultadosComparaveis} atingiram` : 'Aguardando dados'}
              sub="Quantos plantios colheram o esperado ou mais. Só considera resultados com meta definida." darkMode={darkMode} />
          </section>
          <p className="performance-notice"><strong>Meta é o que você espera colher. Real é o que você colheu.</strong> Preço esperado e meta servem para simular; não comprovam vendas nem lucro.</p>
          {(pendencias.length > 0 || (!custosIndisponiveis && conciliacao.unassigned.length > 0)) && <details className="performance-disclosure performance-pending">
            <summary>Pendências para completar a análise</summary>
            <ul>
              {pendencias.map(row => <li key={row._performanceKey}><span>{cicloLabel(row)} — {faltaResultado(row) ? 'falta informar a produção real' : 'meta ainda não definida'}.</span>
                <button type="button" className="crop-performance-report-action" disabled={row._readOnly || salvando || planLoading}
                  onClick={() => abrirEdicao(row)}>{row._readOnly ? 'Somente consulta' : faltaResultado(row) ? 'Informar produção' : 'Definir meta'}</button></li>)}
              {!custosIndisponiveis && conciliacao.unassigned.length > 0 && <li><span>{conciliacao.unassigned.length} gastos da propriedade sem associação a ciclo (todo o histórico).</span>
                <button type="button" className="crop-performance-report-action" disabled={salvando} onClick={conferirGastos}>Conferir gastos</button></li>}
            </ul>
          </details>}

          <details className="performance-disclosure">
            <summary>Ver detalhes e projeções da seleção</summary>
            <div className="performance-grid performance-area-summary">
              <MetricCard label="Área física dos talhões" value={`${fmtNum(resumo.area)} ha`} darkMode={darkMode} />
              <MetricCard label="Área cultivada acumulada" value={`${fmtNum(resumo.areaCultivada)} ha`} darkMode={darkMode} />
            </div>
            {(resumo.comMeta > 0 || resumo.comResultado > 0) && (
            <div className="crop-performance-summary" style={{
              background: cardBg, borderRadius: 16, padding: isMobile ? '16px' : '20px 24px',
              border: `1.5px solid ${border}`, marginBottom: 20,
              boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
            }}>
              <div className="crop-performance-section-heading">
                <div><span>SIMULAÇÃO, NÃO É VENDA</span><h2>Quanto a safra pode gerar?</h2></div>
                <small>Usa a meta e o preço esperado. Os gastos podem estar incompletos; o saldo não é lucro.</small>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fit, minmax(${isMobile ? '130px' : '150px'}, 1fr))`, gap: 10 }}>
                <MetricCard icon={<FaRulerCombined />} label="Área com meta" value={`${fmtNum(resumo.areaComMeta)} ha`} sub={`${Math.round(resumo.coberturaMeta)}% da área`} darkMode={darkMode} />
                <MetricCard icon={<FaSeedling />} label="Produção projetada" value={fmtProducao(resumo.producaoPorUnidade)} sub="por cultura e unidade" darkMode={darkMode} />
                {resumo.comResultado > 0 && <MetricCard icon={<FaCheckCircle />} label="Produção real informada" value={fmtProducao(resumo.producaoReal)} sub="não equivale a receita de vendas" darkMode={darkMode} />}
                {resumo.receita > 0 && (
                  <MetricCard icon={<FaWallet />} label="Receita projetada" value={fmtBRL(resumo.receita)} tone="blue" sub={`${Math.round(resumo.coberturaFinanceira)}% da área`} darkMode={darkMode} />
                )}
                {resumo.receita > 0 && !custosIndisponiveis && resumo.margem != null && (
                  <MetricCard
                    icon={<FaChartLine />}
                    label="Saldo parcial projetado"
                    value={fmtBRL(resumo.margem)}
                    tone={resumo.margem >= 0 ? 'green' : 'red'}
                    sub={`custos associados: ${fmtBRL(resumo.custoFinanceiro)}; não inclui gastos sem ciclo`}
                    darkMode={darkMode}
                  />
                )}
              </div>
              {!custosIndisponiveis && resumo.receita > 0 && resumo.margem == null && <p className="performance-notice">Saldo não calculado: há ciclos com projeção, mas sem custos registrados. Ausência de lançamentos não comprova custo zero.</p>}
            </div>
          )}

          </details>
          <details className="performance-disclosure">
            <summary>Como os números são calculados</summary>
            <dl className="performance-glossary">
              <div><dt>Produtividade</dt><dd>Quantidade colhida em cada hectare (ha). Produção total = produtividade × área.</dd></div>
              <div><dt>Receita projetada</dt><dd>Meta por hectare × área × preço esperado por unidade. Não é uma venda registrada.</dd></div>
              <div><dt>Saldo parcial projetado</dt><dd>Receita projetada menos os gastos associados ao plantio. Não inclui necessariamente todos os custos, impostos ou outras despesas.</dd></div>
              <div><dt>Preço para cobrir os gastos</dt><dd>Gastos registrados ÷ produção planejada. É o preço por unidade que cobriria somente esses gastos se a meta fosse atingida.</dd></div>
            </dl>
            <p className="performance-notice">Cada ciclo é identificado por cultura, talhão e data de plantio, sem agrupar safras por aproximação. A data prevista não confirma colheita. Em andamento inclui plantios planejados; concluídas exigem colheita efetiva.</p>
            <p className="performance-notice">Custos só entram no ciclo quando data e talhão correspondem a um único plantio. Gastos gerais, pré/pós-plantio e ambiguidades ficam na visão Geral, sem rateio. Ausência de lançamentos não comprova custo zero.</p>
            <p className="performance-notice">A produtividade média usa a área com resultado informado, separada por cultura e unidade. Na ausência de resultado, mostra a meta identificada como projeção. Resultados parciais não são uma média de todos os ciclos.</p>
            <p className="performance-notice">O histórico usa área, metas, cotações e registros atuais, não um fechamento imutável.</p>
          </details>

          {/* ── Banner de upgrade para starter ──────────────────────────────── */}
          {isStarter && !planLoading && (
            <div
              className="crop-performance-upgrade"
              onClick={() => setShowUpgrade(true)}
              style={{
                background: darkMode ? '#1a1a2e' : '#f3e5f5',
                border: `1.5px solid ${darkMode ? '#4a235a' : '#ce93d8'}`,
                borderRadius: 12, padding: '12px 16px', marginBottom: 8,
                display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer',
              }}
            >
              <span style={{ fontSize: 22 }}><FaLock /></span>
              <div>
                <div style={{ fontWeight: 700, fontSize: 13, color: textPrimary }}>Estimativas bloqueadas no plano Starter</div>
                <div style={{ fontSize: 13, color: textSecondary, marginTop: 2 }}>Consulte os planos disponíveis para definir metas de produtividade, cotações e margens.</div>
              </div>
              <span style={{ marginLeft: 'auto', fontSize: 13, fontWeight: 700, color: 'var(--crop-blue)', whiteSpace: 'nowrap' }}>Ver planos →</span>
            </div>
          )}

          {/* ── Filtro por status ────────────────────────────────────────────── */}
          <div className="crop-performance-filter-row">
          <h2 className="performance-list-title">3. Complete os dados por talhão <small>({talhoesFiltrados.length} {plantioLabel})</small></h2>
          <div className="performance-export-actions" role="group" aria-label="Exportar ciclos agrícolas">
            <button className="crop-performance-report-action" onClick={() => exportarRelatorio('xlsx')} disabled={gerandoRelatorio || planLoading}>
              <FaFileExcel aria-hidden="true" /> {gerandoRelatorio ? 'Gerando…' : 'Exportar XLSX'}
            </button>
            <button className="crop-performance-report-action" onClick={() => exportarRelatorio('pdf')} disabled={gerandoRelatorio || planLoading}>
              <FaFilePdf aria-hidden="true" /> {gerandoRelatorio ? 'Gerando…' : 'Exportar PDF'}
            </button>
          </div>
          </div>

          {/* ── Cards por talhão ──────────────────────────────────────────────── */}
          {talhoesFiltrados.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: textSecondary }}>
              <div style={{ fontSize: 40, marginBottom: 10 }}>🔍</div>
              Nenhum talhão encontrado neste filtro.
              {filtroStatus !== 'todos' && <button type="button" className="crop-performance-report-action" onClick={() => setFiltroStatus('todos')}>Ver todas as safras</button>}
            </div>
          ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {talhoesFiltrados.map(t => {
              const est = estimativas[t._performanceKey];
              const { area, cost: custo, production: prod, revenue: receita, margin: margem, breakEven: custoUnid, realProduction: producaoReal } = cycleMetrics(t, est, custosIndisponiveis ? {} : custos);
              const isEditing = editandoId === t._performanceKey;
              const unidade = (isEditing ? form.unidade : est?.unidade) || UNIDADE_CULTURA[t.cultura] || 'sc';
              const distanciaEquilibrio = custoUnid > 0 && est?.cotacao
                ? ((parseFloat(est.cotacao) - custoUnid) / custoUnid) * 100
                : null;
              const desvioMeta = hasValue(est?.real) && Number(est?.produtividade) > 0
                ? ((parseFloat(est.real) - parseFloat(est.produtividade)) / parseFloat(est.produtividade)) * 100
                : null;
              const colhido = isColhido(t);
              const planejado = isPlanejado(t);
              const readOnly = t._readOnly;

              return (
                <article className="crop-performance-plot" aria-label={cicloLabel(t)} key={t._performanceKey} style={{
                  background: cardBg, borderRadius: 14,
                  border: `1.5px solid ${border}`,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.05)', overflow: 'hidden',
                }}>
                  {/* ── Cabeçalho do card ──────────────────────────────────────── */}
                  <div className="crop-performance-plot-header" style={{
                    padding: isMobile ? '12px 14px' : '14px 18px',
                    background: colhido
                      ? (darkMode ? '#0f1f10' : '#f1f8e9')
                      : planejado
                        ? (darkMode ? '#2b2110' : '#fff7e4')
                        : (darkMode ? '#0d1628' : '#e8f4fd'),
                    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10,
                  }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: 15, color: textPrimary }}>{t.nome}</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 12px', marginTop: 4 }}>
                        <span style={{ fontSize: 13, color: textSecondary }}><FaSeedling /> {t.cultura}</span>
                        <span className="crop-performance-season-badge">Plantio {t.data_plantio.split('-').reverse().join('/')}</span>
                        {t.area && <span style={{ fontSize: 13, color: textSecondary }}><FaRulerCombined /> {t.area} ha</span>}
                        {colhido && (
                          <span style={{ fontSize: 13, color: 'var(--crop-green)', fontWeight: 600 }}><FaCheckCircle /> Concluída</span>
                        )}
                        {planejado && (
                          <span style={{ fontSize: 13, color: 'var(--crop-amber)', fontWeight: 700 }}>🗓️ Pré-plantio</span>
                        )}
                        {!colhido && !planejado && <span style={{ fontSize: 13, color: textSecondary }}>Em andamento</span>}
                      </div>
                    </div>
                    {!isEditing && (
                      <button
                        className="crop-performance-edit-action"
                        onClick={() => isStarter ? setShowUpgrade(true) : abrirEdicao(t)}
                        disabled={readOnly || salvando || planLoading}
                        title={readOnly ? 'Plantio físico sem vínculo para gravar metas; disponível para consulta.' : undefined}
                        style={{
                          flexShrink: 0,
                          background: readOnly
                            ? (darkMode ? '#202938' : '#edf2f5')
                            : isStarter
                            ? (darkMode ? '#2a2a2a' : '#f5f5f5')
                            : est ? (darkMode ? '#1e3a1e' : '#e8f5e9') : 'linear-gradient(135deg, #43a047, #2e7d32)',
                          color: readOnly ? textSecondary : isStarter ? (darkMode ? '#9ca3af' : '#9e9e9e') : est ? '#43a047' : '#fff',
                          border: readOnly ? `1.5px solid ${border}` : isStarter ? `1.5px solid ${darkMode ? '#374151' : '#e0e0e0'}` : est ? '1.5px solid #43a047' : 'none',
                          borderRadius: 8,
                          padding: isMobile ? '12px 16px' : '6px 14px', minHeight: 44,
                          fontWeight: 600, fontSize: 13, cursor: readOnly ? 'not-allowed' : 'pointer',
                          WebkitTapHighlightColor: 'transparent',
                        }}
                      >
                        {readOnly ? 'Somente consulta' : isStarter ? <><FaLock /> Pro</> : faltaResultado(t) ? 'Informar produção' : est ? <><FaPen /> Editar</> : <><FaBullseye /> Definir meta</>}
                      </button>
                    )}
                  </div>
                  {!isEditing && <dl className="performance-cycle-summary">
                    <div><dt>{hasValue(est?.real) ? 'Produtividade real' : 'Meta de produtividade'}</dt>
                      <dd>{hasValue(est?.real) ? `${fmtNum(est.real)} ${unidade}/ha` : Number(est?.produtividade) > 0 ? `${fmtNum(est.produtividade)} ${unidade}/ha` : 'Não informada'}</dd></div>
                    <div><dt>Gastos registrados</dt><dd>{custosIndisponiveis ? 'Indisponíveis' : custo != null ? fmtBRL(custo) : 'Sem registros'}</dd></div>
                  </dl>}

                  {/* ── Formulário de edição ───────────────────────────────────── */}
                  {isEditing && (
                    <form ref={editorRef} className="crop-performance-editor" noValidate onSubmit={event => { event.preventDefault(); salvar(t); }} style={{
                      padding: isMobile ? '14px' : '16px 18px',
                      borderBottom: `1px solid ${border}`,
                      background: darkMode ? '#0a1525' : '#f8fbff',
                    }}>
                      <h3>Informe os dados deste plantio</h3>
                      <p className="performance-notice">Preencha apenas o que souber. Campos vazios ficam sem informação; use 0 na produtividade real somente se não houve produção. Os exemplos são fictícios.</p>
                      <fieldset className="performance-editor-fields" disabled={salvando}>
                      <label className="performance-unit-field" htmlFor={`unidade-${t._performanceKey}`}>Unidade da produção
                        <select id={`unidade-${t._performanceKey}`} value={form.unidade}
                          aria-describedby={`unidade-ajuda-${t._performanceKey}`}
                          onChange={event => setForm(previous => ({ ...previous, unidade: event.target.value }))}>
                          {!UNIDADES_PRODUCAO[form.unidade] && <option value={form.unidade}>{form.unidade} (unidade cadastrada)</option>}
                          {Object.entries(UNIDADES_PRODUCAO).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                        </select>
                      </label>
                      <p id={`unidade-ajuda-${t._performanceKey}`} className="performance-notice">Use a mesma unidade para meta, colheita e preço. Ao trocar a unidade, revise os valores: o sistema não faz conversões. Para sacas, confira o peso negociado do produto.</p>
                      <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fit, minmax(${isMobile ? '150px' : '180px'}, 1fr))`, gap: 12, marginBottom: 14 }}>
                        {colhido && (
                          <div>
                            <label htmlFor={`real-${t._performanceKey}`} style={{ fontSize: 13, color: textSecondary, fontWeight: 600, display: 'block', marginBottom: 5 }}>
                              Quanto colheu por hectare? ({unidade}/ha)
                            </label>
                            <input id={`real-${t._performanceKey}`}
                              aria-describedby={`real-ajuda-${t._performanceKey}`}
                              type="number" step="any" min="0"
                              value={form.real}
                              onChange={e => setForm(f => ({ ...f, real: e.target.value }))}
                              placeholder="Resultado da colheita"
                              style={inputStyle}
                            />
                            <p id={`real-ajuda-${t._performanceKey}`} className="performance-field-help">Informe por hectare, não a colheita total. Se colheu 600 {unidade} em 10 ha, informe 60.</p>
                          </div>
                        )}
                        <div>
                          <label htmlFor={`meta-${t._performanceKey}`} style={{ fontSize: 13, color: textSecondary, display: 'block', marginBottom: 5 }}>
                            Quanto espera colher por hectare? ({unidade}/ha)
                          </label>
                          <input
                            id={`meta-${t._performanceKey}`}
                            aria-describedby={`meta-ajuda-${t._performanceKey}`}
                            type="number" step="any" min="0"
                            value={form.produtividade}
                            onChange={e => setForm(f => ({ ...f, produtividade: e.target.value }))}
                            placeholder="Ex.: 60"
                            style={inputStyle}
                          />
                          <p id={`meta-ajuda-${t._performanceKey}`} className="performance-field-help">Esta é a sua meta. Use o histórico do talhão como referência, não um valor de exemplo.</p>
                        </div>
                        <div>
                          <label htmlFor={`preco-${t._performanceKey}`} style={{ fontSize: 13, color: textSecondary, display: 'block', marginBottom: 5 }}>
                            Por quanto espera vender? (R$/{unidade})
                          </label>
                          <input
                            id={`preco-${t._performanceKey}`}
                            aria-describedby={`preco-ajuda-${t._performanceKey}`}
                            type="number" step="0.01" min="0"
                            value={form.cotacao}
                            onChange={e => setForm(f => ({ ...f, cotacao: e.target.value }))}
                            placeholder="Preço por unidade"
                            style={inputStyle}
                          />
                          <p id={`preco-ajuda-${t._performanceKey}`} className="performance-field-help">Preço esperado por unidade, não o valor total da venda. Não é uma cotação de mercado atualizada.</p>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button
                          className="performance-save-action"
                          type="submit"
                          disabled={salvando}
                          style={{
                            background: salvando ? '#9e9e9e' : 'linear-gradient(135deg, #43a047, #2e7d32)',
                            color: '#fff', border: 'none', borderRadius: 8,
                            padding: isMobile ? '13px 22px' : '9px 22px',
                            minHeight: 44, fontWeight: 700,
                            cursor: salvando ? 'not-allowed' : 'pointer', fontSize: 13,
                            WebkitTapHighlightColor: 'transparent',
                          }}
                        >
                          {salvando ? 'Salvando…' : <><FaCheck /> Salvar</>}
                        </button>
                        <button
                          type="button"
                          onClick={() => setEditandoId(null)}
                          disabled={salvando}
                          style={{
                            background: 'transparent', color: textSecondary,
                            border: `1.5px solid ${border}`, borderRadius: 8,
                            padding: isMobile ? '13px 16px' : '9px 16px',
                            minHeight: 44, fontWeight: 600, cursor: 'pointer', fontSize: 13,
                            WebkitTapHighlightColor: 'transparent',
                          }}
                        >
                          Cancelar
                        </button>
                      </div>
                      </fieldset>
                    </form>
                  )}

                  <details className="performance-disclosure performance-cycle-details">
                    <summary>Ver detalhes do ciclo</summary>
                    <p className="performance-notice">Colheita efetiva: {t.data_colheita_efetiva ? t.data_colheita_efetiva.split('-').reverse().join('/') : 'Não registrada'}</p>
                  {est && (est.produtividade || est.cotacao || hasValue(est.real) || custo != null) && !isEditing && (
                    <div style={{ padding: isMobile ? '12px 14px' : '14px 18px' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fit, minmax(${isMobile ? '130px' : '150px'}, 1fr))`, gap: 10 }}>
                        {est.produtividade && (
                          <MetricCard icon={<FaBullseye />} label={`Meta (${unidade}/ha)`} value={`${fmtNum(est.produtividade)} ${unidade}/ha`} tone="blue" darkMode={darkMode} />
                        )}
                        {prod != null && (
                          <MetricCard icon={<FaSeedling />} label={`Produção planejada (${unidade})`} value={`${fmtNum(prod, 0)} ${unidade}`} sub="meta por hectare × área do talhão" darkMode={darkMode} />
                        )}
                        {receita != null && (
                          <MetricCard icon={<FaCoins />} label="Receita estimada" value={fmtBRL(receita)} tone="blue" darkMode={darkMode} />
                        )}
                        {custo != null && (
                          <MetricCard icon={<FaClipboardList />} label="Custo registrado" value={fmtBRL(custo)} tone="amber" sub="caderno e eventos; associado por data" darkMode={darkMode} />
                        )}
                        {custo != null && area > 0 && <MetricCard icon={<FaRulerCombined />} label="Custo por hectare" value={fmtBRL(custo / area)} sub="gastos associados ao ciclo" darkMode={darkMode} />}
                        {producaoReal != null && <MetricCard icon={<FaCheckCircle />} label="Produção real" value={`${fmtNum(producaoReal)} ${unidade}`} sub="informada pelo usuário; não é venda" darkMode={darkMode} />}
                        {producaoReal > 0 && custo != null && <MetricCard icon={<FaCoins />} label={`Custo por ${unidade} produzida`} value={fmtBRL(custo / producaoReal)} sub="baseado na produção real" darkMode={darkMode} />}
                        {margem != null && (
                          <MetricCard
                            icon={<FaChartLine />}
                            label="Saldo parcial projetado"
                            value={fmtBRL(margem)}
                            tone={margem >= 0 ? 'green' : 'red'}
                            sub="meta × preço esperado menos gastos associados"
                            darkMode={darkMode}
                          />
                        )}
                        {custoUnid != null && (
                          <MetricCard
                            icon={<FaBalanceScale />}
                            label={`Preço para cobrir os gastos (R$/${unidade})`}
                            value={`R$ ${fmtNum(custoUnid, 2)}/${unidade}`}
                            sub="gastos registrados ÷ produção planejada; não é preço mínimo de lucro"
                            darkMode={darkMode}
                          />
                        )}
                        {hasValue(est.real) && (
                          <MetricCard
                            icon={Number(est.produtividade) > 0 ? (Number(est.real) >= Number(est.produtividade) ? '✅' : '⚠️') : <FaCheckCircle />}
                            label={`Produtividade real (${unidade}/ha)`}
                            value={`${fmtNum(est.real)} ${unidade}/ha`}
                            tone={Number(est.produtividade) > 0 ? (Number(est.real) >= Number(est.produtividade) ? 'green' : 'amber') : undefined}
                            sub={Number(est.produtividade) > 0
                              ? `meta: ${fmtNum(est.produtividade)} — ${parseFloat(est.real) >= parseFloat(est.produtividade) ? '✓ atingida' : '✗ abaixo'}`
                              : undefined}
                            darkMode={darkMode}
                          />
                        )}
                      </div>
                      {(distanciaEquilibrio != null || desvioMeta != null) && (
                        <div className={`crop-performance-plot-insight ${(desvioMeta != null ? desvioMeta : distanciaEquilibrio) >= 0 ? 'is-positive' : 'is-attention'}`}>
                          <FaChartLine />
                          <span>
                            {desvioMeta != null
                              ? `Resultado real ${Math.abs(desvioMeta).toFixed(1)}% ${desvioMeta >= 0 ? 'acima' : 'abaixo'} da meta.`
                              : `Preço esperado ${Math.abs(distanciaEquilibrio).toFixed(1)}% ${distanciaEquilibrio >= 0 ? 'acima' : 'abaixo'} do preço para cobrir os gastos registrados. Não garante lucro.`}
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* ── Sem dados ainda ───────────────────────────────────────── */}
                  {!est && !isEditing && custo == null && !readOnly && (
                    <div style={{ padding: '12px 18px', color: textSecondary, fontSize: 13 }}>
                      Nenhuma estimativa cadastrada. Use a ação do ciclo para informar produtividade ou definir uma meta.
                    </div>
                  )}
                  {readOnly && (
                    <div style={{ padding: '12px 18px', color: textSecondary, fontSize: 13 }}>
                      Plantio físico disponível para consulta. A gravação de metas depende de um vínculo persistido com o cadastro de produtividade; nenhum vínculo será criado por aproximação.
                    </div>
                  )}
                  {t._legacy && <p className="performance-notice">Cadastro legado sem ciclo confirmado. Metas antigas foram preservadas; os gastos permanecem na visão Geral até haver um ciclo compatível.</p>}
                  {!est && !isEditing && custo != null && (
                    <div style={{ padding: isMobile ? '12px 14px' : '12px 18px' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fit, minmax(${isMobile ? '130px' : '150px'}, 1fr))`, gap: 10, marginBottom: 10 }}>
                        <MetricCard icon={<FaClipboardList />} label="Custo registrado" value={fmtBRL(custo)} tone="amber" sub="caderno e eventos; associado por data" darkMode={darkMode} />
                      </div>
                      {!readOnly && <div style={{ fontSize: 13, color: textSecondary }}>Defina a meta de produtividade para calcular a projeção.</div>}
                    </div>
                  )}
                  </details>
                </article>
              );
            })}
          </div>
          )}
          </>}
        </>
      )}

      </>}
      <UpgradeModal
        show={showUpgrade}
        onClose={() => setShowUpgrade(false)}
        recurso="produtividade"
        planoAtual={plano?.tipo || 'starter'}
        usuario={user}
      />
    </div>
  );
}
